import type { ApplicationFull, FileItem } from '@jt/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api, post } from '../api/client';
import { keys, useAppActions, useFiles } from '../api/hooks';
import { useUI } from '../components/overlays';
import { Icon, safeUrl } from '../components/ui';

export const ACCEPT = '.pdf,.docx,.doc,.odt,.png,.jpg,.jpeg,.webp,.txt,.md';

/** Загрузка файла: поля идут до файла, чтобы сервер прочитал их вместе с ним. */
export function uploadFile(file: File, kind: 'resume' | 'attachment', extra: Record<string, string> = {}) {
  const fd = new FormData();
  fd.set('kind', kind);
  for (const [k, v] of Object.entries(extra)) fd.set(k, v);
  fd.set('file', file, file.name);
  return api<FileItem>('POST', '/files', fd);
}

export const fileHref = (f: Pick<FileItem, 'id' | 'stored' | 'linkUrl'>, inline = false) =>
  f.stored ? `/api/files/${f.id}/download${inline ? '?inline=1' : ''}` : safeUrl(f.linkUrl);

export function FilesTab({ a }: { a: ApplicationFull }) {
  const act = useAppActions();
  const ui = useUI();
  const qc = useQueryClient();
  const resumes = useFiles('resume');
  const [resId, setResId] = useState('');
  const [err, setErr] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const attached = new Set(a.files.map((f) => f.id));
  const options = (resumes.data ?? []).filter((r) => !attached.has(r.id));

  const onUpload = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      ui.toast('Файл больше 20 МБ.');
      return;
    }
    ui.toast('Загружаю…');
    try {
      const f = await uploadFile(file, 'attachment');
      await act.attachFile(a.id, f.id);
      void qc.invalidateQueries({ queryKey: ['files'] });
      ui.toast('Файл загружен');
    } catch (e) {
      ui.toast(e instanceof ApiError ? e.message : 'Не загрузилось');
    }
  };

  const addLink = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const url = String(fd.get('url') ?? '').trim();
    if (!url) {
      setErr('Вставьте ссылку.');
      return;
    }
    try {
      const f = await post<FileItem>('/files/link', {
        name: String(fd.get('name') ?? '').trim(),
        url,
        kind: 'link',
      });
      await act.attachFile(a.id, f.id);
      form.reset();
      setErr('');
    } catch (x) {
      setErr(x instanceof ApiError ? (Object.values(x.fields)[0] ?? x.message) : 'Не сохранилось');
    }
  };

  return (
    <>
      {a.files.length ? (
        a.files.map((f) => (
          <div className="fileRow" key={f.id}>
            <Icon name={f.kind === 'resume' ? 'file' : f.kind === 'link' ? 'link' : 'paperclip'} />
            <a className="fn" href={fileHref(f)} target="_blank" rel="noopener noreferrer">
              <b>{f.kind === 'resume' ? 'Резюме · ' : ''}</b>
              {f.name}
            </a>
            <button className="x" onClick={() => act.detachFile(a.id, f.id)} aria-label="Открепить">
              ✕
            </button>
          </div>
        ))
      ) : (
        <p className="muted" style={{ marginTop: 0 }}>
          Прикрепите версию резюме, которую отправили, тестовое задание или оффер.
        </p>
      )}
      {resumes.data?.length ? (
        <div className="toolbar" style={{ margin: '10px 0' }}>
          <label className="selw" style={{ flex: '1 1 180px', minHeight: 40 }}>
            <Icon name="file" size={16} />
            <select value={resId} onChange={(e) => setResId(e.target.value)} aria-label="Резюме">
              <option value="">Выберите резюме</option>
              {options.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.lang ? ` · ${r.lang}` : ''}
                </option>
              ))}
            </select>
          </label>
          <button
            className="btn sm"
            disabled={!resId}
            onClick={() =>
              act.attachFile(a.id, resId).then(() => {
                setResId('');
                void qc.invalidateQueries({ queryKey: keys.files('resume') });
                ui.toast('Резюме прикреплено');
              })
            }
          >
            Прикрепить резюме
          </button>
        </div>
      ) : (
        <p className="sm muted">
          Версии резюме добавляются в разделе <Link to="/resumes">«Резюме»</Link>.
        </p>
      )}
      <div className="btnrow">
        <button className="btn sm" onClick={() => input.current?.click()}>
          <Icon name="upload" size={16} /> Загрузить файл
        </button>
        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={(e) => {
            void onUpload(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </div>
      <form className="pform" onSubmit={addLink}>
        <div className="grid">
          <label className="f">
            Название
            <input name="name" placeholder="Тестовое задание" />
          </label>
          <label className="f">
            Ссылка
            <input name="url" type="url" placeholder="https://" />
          </label>
        </div>
        {err && <p className="fnote err">{err}</p>}
        <div className="btnrow" style={{ marginTop: 8 }}>
          <button className="btn sm" type="submit">
            <Icon name="link" size={16} /> Добавить ссылку
          </button>
        </div>
      </form>
    </>
  );
}
